import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { selectCourse } from '../../../store/selectors';
import { Course } from '../../../types/types';
import { AssignmentWizardPageProps } from '../AssignmentWizard';
import HelpTag from '../../core/display/HelpTag';

export interface Grading1Props {
  benchmarkGradingEnabled: boolean;
  teacherReviewOverridesStudents: boolean;
  skipAsyncAccuracyCheck?: boolean;
  completionGradingRequireReviews: boolean | undefined;
  completionGrading?: boolean;
  asyncUngraded?: boolean;
  asyncTaskGradeOnly?: boolean;
}

function GradingPage1({ assignmentSettings, updateSettings }: AssignmentWizardPageProps<Grading1Props>): JSX.Element {
  const course = useSelector(selectCourse) as Course;

  const [benchmarkGradingEnabled, setBenchmarkGradingEnabled] = useState(assignmentSettings.benchmarkGradingEnabled);
  const [teacherReviewOverridesStudents, setTeacherReviewOverridesStudents] = useState(
    assignmentSettings.teacherReviewOverridesStudents,
  );
  const [skipAsyncAccuracyCheck, setSkipAsyncAccuracyCheck] = useState(assignmentSettings.skipAsyncAccuracyCheck);
  const [completionGradingRequireReviews, setCompletionGradingRequireReviews] = useState(
    assignmentSettings.completionGradingRequireReviews,
  );
  const [completionGrading, setCompletionGrading] = useState(assignmentSettings.completionGrading);
  const [asyncUngraded, setAsyncUngraded] = useState(assignmentSettings.asyncUngraded);
  const [asyncTaskGradeOnly, setAsyncTaskGradeOnly] = useState(assignmentSettings.asyncTaskGradeOnly);

  useEffect(() => {
    updateSettings({
      benchmarkGradingEnabled,
      teacherReviewOverridesStudents,
      skipAsyncAccuracyCheck,
      completionGradingRequireReviews,
      completionGrading,
      asyncUngraded,
      asyncTaskGradeOnly,
    });
  }, [
    updateSettings,
    benchmarkGradingEnabled,
    teacherReviewOverridesStudents,
    skipAsyncAccuracyCheck,
    completionGradingRequireReviews,
    completionGrading,
    asyncUngraded,
    asyncTaskGradeOnly,
  ]);

  return (
    <>
      <h3>Instructor Involvement</h3>
      <div className="sub-section">
        {!course.asyncEnabled &&
        !assignmentSettings.peerEvaluationOnly &&
        !assignmentSettings.instructorUpload &&
        !assignmentSettings.groupFormationDeadline ? (
          <fieldset>
            <legend>Grading Style</legend>
            <div className="rad-radio-btn">
              <input
                id="curved"
                type="radio"
                name="benchmarkGradingEnabled"
                value="false"
                checked={!benchmarkGradingEnabled}
                onChange={(e) => setBenchmarkGradingEnabled(e.target.value === 'true')}
              />
              <label htmlFor="curved">Curved</label>
            </div>
            <div className="rad-radio-btn">
              <input
                id="benchmarked"
                type="radio"
                name="benchmarkGradingEnabled"
                value="true"
                checked={benchmarkGradingEnabled}
                onChange={(e) => setBenchmarkGradingEnabled(e.target.value === 'true')}
              />
              <label htmlFor="benchmarked">Benchmarked</label>
            </div>
            {!benchmarkGradingEnabled ? (
              <p>
                Curved grading uses the mean and standard deviation set by the instructor to generate the submission and
                reviewing grades.
              </p>
            ) : (
              <p>
                Benchmarking requires the instructor to grade ten student submissions. These benchmarks are used to
                determine the grades for the rest of the submissions.{' '}
                <a
                  href="https://peerceptiv.zendesk.com/hc/en-us/articles/360019272514-Step-By-Step-Guide-to-Benchmark-Grading"
                  target="_blank"
                  rel="noreferrer"
                >
                  Learn more
                </a>
              </p>
            )}
          </fieldset>
        ) : null}

        {!course.asyncEnabled && !assignmentSettings.peerEvaluationOnly && !assignmentSettings.instructorUpload ? (
          <fieldset>
            <legend>Instructor Review Impact</legend>
            <div className="rad-radio-btn">
              <input
                id="regularReview"
                type="radio"
                name="teacherReviewOverridesStudents"
                value="false"
                checked={!teacherReviewOverridesStudents}
                onChange={(e) => setTeacherReviewOverridesStudents(e.target.value === 'true')}
              />
              <label htmlFor="regularReview">Regular Review</label>
            </div>
            <div className="rad-radio-btn">
              <input
                id="overrideReviews"
                type="radio"
                name="teacherReviewOverridesStudents"
                value="true"
                checked={teacherReviewOverridesStudents}
                onChange={(e) => setTeacherReviewOverridesStudents(e.target.value === 'true')}
              />
              <label htmlFor="overrideReviews">Override Student Reviews</label>
            </div>
          </fieldset>
        ) : null}
        {course.asyncEnabled ? (
          <fieldset>
            <legend>Enforce Async Accuracy Check</legend>
            <div className="accuracy-check-instructions">
              <p>
                When the accuracy check is enabled, the algorithm will check for review rating accuracy. If the student
                does not pass the accuracy threshold, an overall grade of zero will be issued.
              </p>
            </div>
            <div className="rad-radio-btn">
              <input
                id="asyncNoAccuracyCheck"
                type="radio"
                name="accuracyCheck"
                value="true"
                checked={!skipAsyncAccuracyCheck}
                onChange={(e) => setSkipAsyncAccuracyCheck(e.target.value === 'false')}
              />
              <label htmlFor="asyncNoAccuracyCheck">Yes</label>
            </div>
            <div className="rad-radio-btn">
              <input
                id="asyncAccuracyCheck"
                type="radio"
                name="accuracyCheck"
                value="false"
                checked={skipAsyncAccuracyCheck}
                onChange={(e) => setSkipAsyncAccuracyCheck(e.target.value === 'false')}
              />
              <label htmlFor="asyncAccuracyCheck">No</label>
            </div>
          </fieldset>
        ) : null}
        {course.asyncEnabled ? (
          <fieldset>
            <legend>
              How would you like grades to be calculated?{' '}
              <HelpTag type="modal" heading="Async Grading Selection">
                <p>
                  <p>
                    <b>Submission Grade Only</b> - The overall grade will be based on the average peer and instructor
                    ratings received by the submission.
                    <br />
                  </p>
                  <p>
                    <b>Task Grade Only</b> - The overall grade will be based on the percentage of tasks completed versus
                    tasks required.
                    <br />
                  </p>
                  <p>
                    <b>Ungraded - Completion Only</b> - There will be no grade scores generated. A student will receive
                    a pass/fail completion result when their required tasks are completed or the assignment is closed.
                  </p>
                </p>
              </HelpTag>
            </legend>
            <div className="rad-radio-btn">
              <input
                id="submission-based"
                type="radio"
                name="asyncGrading"
                value="submission-based"
                checked={!asyncUngraded && !asyncTaskGradeOnly && !completionGrading}
                onChange={(e) => {
                  if (e.target.value === 'submission-based') {
                    setAsyncUngraded(false);
                    setAsyncTaskGradeOnly(false);
                    setCompletionGrading(false);
                  }
                }}
              />
              <label htmlFor="submission-based">Submission Grading</label>
            </div>

            <div className="rad-radio-btn">
              <input
                id="task-grade-based"
                type="radio"
                name="asyncGrading"
                value="task-grade-based"
                checked={asyncTaskGradeOnly}
                onChange={(e) => {
                  if (e.target.value === 'task-grade-based') {
                    setAsyncUngraded(false);
                    setAsyncTaskGradeOnly(true);
                    setCompletionGrading(false);
                  }
                }}
              />
              <label htmlFor="task-grade-based">Task Grading Only</label>
            </div>

            <div className="rad-radio-btn">
              <input
                id="completion-based"
                type="radio"
                name="asyncGrading"
                value="completion-based"
                checked={asyncUngraded && completionGrading}
                onChange={(e) => {
                  if (e.target.value === 'completion-based') {
                    setAsyncUngraded(true);
                    setCompletionGrading(true);
                    setAsyncTaskGradeOnly(false);
                  }
                }}
              />
              <label htmlFor="completion-based">Ungraded - Completion Only</label>
            </div>
          </fieldset>
        ) : null}
        {course.asyncEnabled && completionGrading ? (
          <fieldset>
            <legend>When will students be marked complete?</legend>
            <div className="rad-radio-btn">
              <input
                id="aync-result-receive-task-complete"
                type="radio"
                name="asyncResultReceiveComplete"
                value="TASK_COMPLETE"
                checked={!completionGradingRequireReviews}
                onChange={() => setCompletionGradingRequireReviews(false)}
              />
              <label htmlFor="asyncResultReceiveComplete">After finishing their assigned tasks </label>
            </div>
            <div className="rad-radio-btn">
              <input
                id="aync-result-receive-task-complete-and-review-received"
                type="radio"
                name="asyncResultReleaseWhenBothComplete"
                value="TASK_COMPLETE_AND_REVIEW_RECEIVED"
                checked={completionGradingRequireReviews}
                onChange={() => setCompletionGradingRequireReviews(true)}
              />
              <label htmlFor="asyncResultReleaseWhenBothComplete">
                After finishing their assigned tasks AND receiving reviews
              </label>
            </div>
          </fieldset>
        ) : null}
      </div>
    </>
  );
}

export default GradingPage1;
